if (!customElements.get('vl-pdp-sticky-anchor-nav')) {
  class VLPDPStickyAnchorNav extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.frame = null;
      this.items = [];
      this.activeIndex = -1;
      this.currentStickyTop = null;
      this.headerObserver = null;
      this.headerResizeObserver = null;
      this.headerAbortController = null;
      this.headerTrackingUntil = 0;
      this.alignmentTimer = null;
      this.pendingTarget = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleHashChange = this.handleHashChange.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleAbTestChange = this.handleAbTestChange.bind(this);
      this.handleHeaderChange = this.handleHeaderChange.bind(this);
    }

    connectedCallback() {
      if (this.abortController) return;

      this.scroller = this.querySelector('[data-vl-sticky-anchor-scroller]');
      this.links = [...this.querySelectorAll('[data-vl-sticky-anchor-link]')];
      if (!this.scroller || this.links.length === 0) return;

      this.stickySection = this.closest('.vl-pdp-sticky-anchor-nav-section') || this;
      this.headerSection = document.querySelector('.shopify-section-header');

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener('click', this.handleClick, { signal });
      window.addEventListener('scroll', this.handleScroll, { passive: true, signal });
      window.addEventListener('resize', this.handleResize, { passive: true, signal });
      window.addEventListener('hashchange', this.handleHashChange, { signal });
      document.addEventListener('shopify:section:load', this.handleSectionLoad, { signal });
      document.addEventListener('vl-abtest:change', this.handleAbTestChange, { signal });

      this.observeHeader();
      this.refreshTargets();
      this.scheduleUpdate(true);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      if (this.alignmentTimer !== null) clearTimeout(this.alignmentTimer);
      this.headerObserver?.disconnect();
      this.headerResizeObserver?.disconnect();
      this.headerAbortController?.abort();

      this.abortController = null;
      this.frame = null;
      this.alignmentTimer = null;
      this.pendingTarget = null;
      this.headerObserver = null;
      this.headerResizeObserver = null;
      this.headerAbortController = null;
      this.headerTrackingUntil = 0;
      this.items = [];
      this.activeIndex = -1;
    }

    handleClick(event) {
      const link = event.target.closest?.('[data-vl-sticky-anchor-link]');
      if (!link || !this.contains(link)) return;

      const item = this.items.find((entry) => entry.link === link);
      if (!item?.target) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      this.pendingTarget = item.target;
      this.scheduleTargetAlignment();
      this.scrollToTarget(item.target);
      this.setActiveItem(item.index, true);

      const hash = `#${item.target.id}`;
      if (window.location.hash !== hash) window.history.pushState(null, '', hash);
    }

    handleScroll() {
      this.scheduleUpdate();

      if (!this.pendingTarget) return;
      this.scheduleTargetAlignment();
    }

    handleResize() {
      this.refreshTargets();
      this.scheduleUpdate(true);
    }

    handleHashChange() {
      const item = this.items.find((entry) => entry.target && `#${entry.target.id}` === window.location.hash);
      if (item) this.setActiveItem(item.index, true);
    }

    handleSectionLoad() {
      this.headerSection = document.querySelector('.shopify-section-header');
      this.observeHeader();
      this.refreshTargets();
      this.scheduleUpdate(true);
    }

    handleAbTestChange() {
      this.refreshTargets();
      this.scheduleUpdate(true);
    }

    handleHeaderChange() {
      this.headerTrackingUntil = performance.now() + 400;
      this.scheduleUpdate(true);
    }

    observeHeader() {
      this.headerObserver?.disconnect();
      this.headerResizeObserver?.disconnect();
      this.headerAbortController?.abort();

      if (!this.headerSection) return;

      this.headerAbortController = new AbortController();
      const { signal } = this.headerAbortController;

      this.headerObserver = new MutationObserver(this.handleHeaderChange);
      this.headerObserver.observe(this.headerSection, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class', 'style', 'visible', 'dynamic-sticky'],
      });

      if ('ResizeObserver' in window) {
        this.headerResizeObserver = new ResizeObserver(this.handleHeaderChange);
        this.headerResizeObserver.observe(this.headerSection);
      }

      this.headerSection.addEventListener('transitionrun', this.handleHeaderChange, { signal });
      this.headerSection.addEventListener('transitionend', this.handleHeaderChange, { signal });
    }

    refreshTargets() {
      const isEditor = Boolean(window.Shopify?.designMode);

      this.items = this.links.map((link, index) => {
        const id = link.dataset.anchorId;
        const target = id ? document.getElementById(id) : null;
        const isAvailable = Boolean(target);

        link.classList.toggle('is-unavailable', !isAvailable);
        link.toggleAttribute('aria-disabled', !isAvailable);
        link.hidden = !isAvailable && !isEditor;

        return { link, target, index };
      });
    }

    scrollToTarget(target) {
      const stickyTop = this.getStickyTop();
      const styles = getComputedStyle(this);
      const scrollGap = Number.parseFloat(styles.getPropertyValue('--vl-pdp-sticky-nav-scroll-gap')) || 0;
      const top = target.getBoundingClientRect().top + window.scrollY - stickyTop - this.offsetHeight - scrollGap;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    finishTargetAlignment() {
      const target = this.pendingTarget;
      this.pendingTarget = null;
      this.alignmentTimer = null;
      if (!target?.isConnected) return;

      this.updateStickyOffset();

      const styles = getComputedStyle(this);
      const scrollGap = Number.parseFloat(styles.getPropertyValue('--vl-pdp-sticky-nav-scroll-gap')) || 0;
      const expectedTop = this.getStickyTop() + this.offsetHeight + scrollGap;
      const difference = target.getBoundingClientRect().top - expectedTop;

      if (Math.abs(difference) > 2) window.scrollBy({ top: difference, behavior: 'auto' });
    }

    scheduleTargetAlignment() {
      if (this.alignmentTimer !== null) clearTimeout(this.alignmentTimer);
      this.alignmentTimer = window.setTimeout(() => this.finishTargetAlignment(), 160);
    }

    scheduleUpdate(force = false) {
      if (this.frame !== null && !force) return;
      if (this.frame !== null) cancelAnimationFrame(this.frame);

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateStickyOffset();
        this.updateActiveItem();

        if (performance.now() < this.headerTrackingUntil) this.scheduleUpdate();
      });
    }

    updateStickyOffset() {
      const nextTop = this.getVisibleHeaderBottom();
      if (Math.abs(nextTop - (this.currentStickyTop ?? -1)) < 0.5) return;

      this.currentStickyTop = nextTop;
      this.stickySection.style.setProperty('--vl-pdp-sticky-nav-current-top', `${nextTop}px`);
    }

    getVisibleHeaderBottom() {
      const header = this.headerSection;
      if (!header?.isConnected) return this.getConfiguredStickyTop();

      const styles = getComputedStyle(header);
      const isSticky = styles.position === 'sticky' || styles.position === 'fixed';

      if (!isSticky || styles.display === 'none' || styles.visibility === 'hidden') return 0;

      const rect = header.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0;

      return Math.max(0, Math.min(window.innerHeight, rect.bottom));
    }

    getConfiguredStickyTop() {
      const styles = getComputedStyle(this.stickySection);
      const property = window.matchMedia('(max-width: 767px)').matches
        ? '--vl-pdp-sticky-nav-top-mobile'
        : '--vl-pdp-sticky-nav-top';

      return Number.parseFloat(styles.getPropertyValue(property)) || 0;
    }

    updateActiveItem() {
      const availableItems = this.items.filter((item) => item.target && !item.link.hidden);
      if (availableItems.length === 0) return;

      const stickyTop = this.getStickyTop();
      const activationLine = stickyTop + this.offsetHeight + 24;
      let activeItem = availableItems[0];

      availableItems.forEach((item) => {
        if (item.target.getBoundingClientRect().top <= activationLine) activeItem = item;
      });

      this.setActiveItem(activeItem.index);
    }

    getStickyTop() {
      return this.currentStickyTop ?? this.getConfiguredStickyTop();
    }

    setActiveItem(index, ensureVisible = false) {
      if (index === this.activeIndex && !ensureVisible) return;

      this.activeIndex = index;
      let activeLink = null;

      this.items.forEach((item) => {
        const isActive = item.index === index;
        item.link.classList.toggle('is-active', isActive);
        if (isActive) {
          item.link.setAttribute('aria-current', 'true');
        } else {
          item.link.removeAttribute('aria-current');
        }
        if (isActive) activeLink = item.link;
      });

      if (activeLink) this.revealLink(activeLink);
    }

    revealLink(link) {
      const scrollerRect = this.scroller.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const padding = 16;

      if (linkRect.left >= scrollerRect.left + padding && linkRect.right <= scrollerRect.right - padding) return;

      const targetLeft =
        this.scroller.scrollLeft +
        linkRect.left -
        scrollerRect.left -
        (scrollerRect.width - linkRect.width) / 2;

      this.scroller.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('vl-pdp-sticky-anchor-nav', VLPDPStickyAnchorNav);
}
